/**
 * A loading/discharge time window is wall clock, not an instant: the stop's date range says on
 * which days it can happen, the window says between which times on such a day. Times are held,
 * submitted and stored as zero-padded `HH:mm` strings throughout.
 */

/**
 * Normalizes what the user typed into a `HH:mm` string.
 *
 * Accepts `6`, `06`, `6:3`, `6:30`, `6.30`, `6,30`, `630` and `0630`.
 * Returns `null` for an empty value and for anything that is not a valid time, so a caller
 * normalizing on blur can simply leave the raw text in place and let validation report it.
 */
export const parseTimeInput = (raw?: string | null): string | null => {
    const value = (raw ?? '').trim();
    if (!value) return null;

    const match = /^(\d{1,4})(?:[:.,](\d{1,2}))?$/.exec(value);
    if (!match) return null;

    const [, digits, separatedMinutes] = match;

    let hours: string;
    let minutes: string;

    if (separatedMinutes !== undefined) {
        // A separator makes the split explicit: everything before it is the hour.
        if (digits.length > 2) return null;
        hours = digits;
        minutes = separatedMinutes;
    } else if (digits.length <= 2) {
        // Bare hour, e.g. "6" or "06".
        hours = digits;
        minutes = '0';
    } else {
        // Packed digits, e.g. "630" or "0630" — the last two are always the minutes.
        hours = digits.slice(0, -2);
        minutes = digits.slice(-2);
    }

    const parsedHours = Number(hours);
    const parsedMinutes = Number(minutes);
    if (parsedHours > 23 || parsedMinutes > 59) return null;

    return `${String(parsedHours).padStart(2, '0')}:${String(parsedMinutes).padStart(2, '0')}`;
};

/** Whether the raw input is either empty or something `parseTimeInput` understands. */
export const isTimeInput = (raw?: string | null): boolean => {
    const value = (raw ?? '').trim();
    return value === '' || parseTimeInput(value) !== null;
};

/**
 * Joins two `HH:mm` strings into a stop's time window, `06:00-14:00`, collapsing to a single
 * `06:00` when there is no end time or both ends are the same — mirroring how the date range is
 * rendered. A window with only an end time keeps the leading separator (`-14:00`) so it cannot be
 * misread as a start time.
 */
export const joinTimeRange = (from?: string | null, to?: string | null): string | null => {
    const start = from || null;
    const end = to || null;

    if (!start) return end === null ? null : `-${end}`;
    if (!end || end === start) return start;

    return `${start}-${end}`;
};

/**
 * Whether a stop's window is ordered. Overnight windows are not representable: the date range says
 * on which days the stop can happen, the window says when on such a day, so the end can never fall
 * before the start. Zero-padded `HH:mm` compares lexicographically in chronological order.
 */
export const isTimeWindowOrdered = (from?: string | null, to?: string | null): boolean => {
    const start = parseTimeInput(from);
    const end = parseTimeInput(to);
    if (start === null || end === null) return true;

    return end >= start;
};
